import {
	ApplicationRestriction,
	AppUserProfile,
	AttendanceImportTableItem,
	Constant,
	EventPaymentMethodGroup,
	FieldConfig,
	ItemWithLocation,
	ItemWithOwnerCenter,
	RepeatDayOfWeekOptions,
	RepeatWeekOptions,
	ScheduleFrequency,
	TimeslotRecurrenceMonthlyMode,
	TimeslotRecurrenceType,
	Volunteer,
	Weekday,
} from '.';
import {
	NEGATIVE_CHIP,
	NEUTRAL_CHIP,
	ACTIVE_CHIP,
	INACTIVE_CHIP,
} from '../mixins/style/chip-mixins';
import { EmptyMultiLangText, MultiLangText } from './locale';
import cloneDeep from 'lodash/cloneDeep';
import { MultiStepFormValidationStatus } from './multi-step-form';
import { PaymentMethodValues } from '../store/types';

export enum EventStatusCode {
	draft = 'draft',
	published = 'published',
	completed = 'completed',
	closed = 'closed',
	cancelled = 'cancelled',
	full = 'full',
}

export enum EventScheduleProgressCode {
	scheduled = 'scheduled',
	ongoing = 'ongoing',
	completed = 'completed',
	cancelled = 'cancelled',
}

export interface EventDto extends Partial<Omit<Event, 'paymentTypes'>> {
	paymentTypes?: (EventPaymentType | Partial<EventPaymentType>)[];

	// these are required
	applicationInfo: EventApplicationInfo;
	mainNotificationEmail: string;
	extraNotificationEmails: string[];
}

export type EventValidations = Partial<
	Record<EventEditStep, EventValidationStatus>
>;

export enum EventType {
	activity = 'activity',
	service = 'service',
}

export enum EventProgress {
	scheduled = 'scheduled',
	ongoing = 'ongoing',
	completed = 'completed',
	cancelled = 'cancelled',
}

export enum EventQuotaType {
	noLimit = 'noLimit',
	totalQouta = 'totalQuota',
}

export enum EventVisibility {
	public = 'public',
	unlisted = 'unlisted',
	members = 'membersOnly',
	publicSuc = 'publicSuc',
}

export interface EventCheckIn {
	id: string;
	time: string | Date;
}

export interface EventBasicInfo {
	eventType?: EventType;
	activityType?: string;
	activityThemes?: string[];
	serviceType?: string;
	targetAudiences?: string[];
}

export interface EventFeeType {
	id?: string;
	optionId?: string;
	title: MultiLangText;
	fee: number | null;
	quota: number | null;
	description?: MultiLangText;
	applicationStart?: Date;
	applicationEnd?: Date;
	// ticketing
	numOfPeople: number | null;
	minPurchase: number | null;
	maxPurchase: number | null;
	//calculated
	totalApplications?: number;
	acceptedApplications?: number;
	isFull?: boolean;
	usedQuota?: number;
}

export interface EventOption {
	id?: string;
	// order index
	position: number;
	event?: Event;
	eventId?: string;
	title: MultiLangText;
	frequency?: ScheduleFrequency;
	remarks: MultiLangText;

	/**
	 * Date & time
	 */
	// Date
	isSingleDay?: boolean;
	startDate: string | null;
	endDate: string | null;
	// Time
	startTime?: string;
	endTime?: string;
	recurrenceType?: TimeslotRecurrenceType;
	recurrenceFrequency?: number;
	recurrenceWeekdays?: Weekday[];
	recurrenceMonthlyMode?: TimeslotRecurrenceMonthlyMode;
	recurrenceRepeatDayOfMonth?: number[];
	recurrenceRepeatWeek?: RepeatWeekOptions;
	recurrenceRepeatDayOfWeek?: RepeatDayOfWeekOptions;
	recurrenceExceptionDates?: string[];
	/**
	 * End of date & time
	 */

	schedules?: EventSchedule[];

	quotaType?: EventQuotaType;
	totalQuota: number | null;

	feeTypes?: EventFeeType[];

	overallStart?: Date;
	overallEnd?: Date;
	totalApplications?: number;
	acceptedApplications?: number;

	isFull?: boolean;
	isMain?: boolean;
	usedQuota?: number;

	createdAt?: Date;
	updatedAt?: Date;
}

export interface EventSchedule {
	id?: string;
	// order index
	position: number;
	option?: EventOption;
	optionId?: string;
	// Date
	isSingleDay: boolean;
	isDateTBC: boolean;
	startDate: string | null;
	endDate: string | null;
	// Time Slot
	isAllDay: boolean;
	isTimeTBC: boolean;
	startTime?: string | null;
	endTime?: string | null;
	progress?: EventScheduleProgressCode;
	rundownSessions?: EventRundownSession[];
	// recurrence
	recurrenceType?: TimeslotRecurrenceType;
	recurrenceFrequency?: number;
	recurrenceWeekdays?: Weekday[];
	recurrenceMonthlyMode?: TimeslotRecurrenceMonthlyMode;
	recurrenceRepeatDayOfMonth?: number[];
	recurrenceRepeatWeek?: RepeatWeekOptions;
	recurrenceRepeatDayOfWeek?: RepeatDayOfWeekOptions;
	recurrenceExceptionDates?: string[];
	volunteerCount?: number;
	usedQuota?: number;
	isMain?: boolean;
	createdAt?: Date;
	updatedAt?: Date;
}

export class EventRundownSession {
	id?: string;
	schedule?: EventSchedule;
	scheduleId?: string;
	title?: MultiLangText;
	description?: MultiLangText;
	startTime?: string;
	endTime?: string;
}

export interface EventScheduleInstance {
	id: string;

	schedule?: EventSchedule;
	scheduleId?: string;

	// Date
	isDateTBC?: boolean;
	startDate?: string;

	// schedule
	isAllDay: boolean;
	isTimeTBC?: boolean;
	startTime?: string;
	endTime?: string;

	progress?: EventScheduleProgressCode;

	confrimPendingCount: number;

	checkedInAttendees: number;
	pendingAttendees: number; //currently used by internal event schedule only, normal one use "no. of applications" for this
}

export enum EventApproval {
	manual = 'manual',
	instant = 'instant',
}
export interface EventApplicationInfo {
	approval: EventApproval;
	minAge: number | null;
	maxAge: number | null;
}

export type EventPaymentMethod = string; // TODO: probably change this when event payment is ready

export interface EventPaymentType {
	id: string;
	event?: Event;
	group?: EventPaymentMethodGroup;
	paymentMethod?: EventPaymentMethod;
	title?: MultiLangText;
	instruction?: MultiLangText;
	isInternal: boolean;
}

export enum EventAdmissionType {
	ticket = 'ticket',
	checkIn = 'checkIn',
}

export enum EventOptionType {
	date = 'date',
	time = 'time',
	custom = 'custom',
}

export interface Event extends ItemWithLocation, ItemWithOwnerCenter {
	id: string;

	ngoId: number;

	status: EventStatusCode;

	// Basic information
	title?: MultiLangText;
	basicInfo?: EventBasicInfo;
	requisitionNumber?: string;
	isInternal?: boolean;

	// Event details
	description?: MultiLangText;
	remarks?: MultiLangText;
	imageUrls?: string[];

	// Event options
	optionType: EventOptionType;
	options?: EventOption[];
	frequency: ScheduleFrequency | null;

	// Admission
	admissionType: EventAdmissionType;
	admissionForAllOptions: boolean;

	// Application settings
	applicationInfo: EventApplicationInfo;
	additionalQuestions?: FieldConfig[];
	contact?: string;
	contactEmail?: string;
	contactPhone?: string;
	visibility?: EventVisibility | null;
	applicationRestrictions: ApplicationRestriction[] | null;
	inviteMembers: boolean | null;

	paymentTypes?: EventPaymentType[];
	overallStart?: Date;
	overallEnd?: Date;
	hasWeekday: boolean;
	hasWeekend: boolean;
	applicationStart?: Date;
	applicationEnd?: Date;

	// Other settings
	progress?: EventProgress;
	validations?: EventValidations;
	eventLink?: string;

	// calculated fields
	pendingApplications?: number;
	acceptedApplications?: number;
	totalApplications?: number;
	totalQuota?: number;
	usedQuota?: number;
	numOfRedeemedTickets?: number;
	completedOptions?: number;
	completedSchedules?: number;
	totalSchedules?: number;
	totalCheckedInAttendees?: number;
	confrimPendingCountTotal: number;

	cancelReason: string;
	addedToReports: string[];

	// notification emails
	mainNotificationEmail: string;
	extraNotificationEmails: string[];

	createdAt: Date;
	updatedAt: Date;
}

export function EmptyEventFactory(
	ownerCenterId: number,
	userEmail: string,
	partialEvent: Partial<Event>,
): EventDto {
	return {
		status: EventStatusCode.draft,
		title: EmptyMultiLangText,
		admissionType: EventAdmissionType.ticket,
		admissionForAllOptions: true,
		description: EmptyMultiLangText,
		remarks: EmptyMultiLangText,
		paymentTypes: [],
		imageUrls: [],
		applicationInfo: {
			approval: EventApproval.instant,
			maxAge: null,
			minAge: null,
		},
		visibility: null,
		applicationRestrictions: null,
		inviteMembers: null,
		ownerCenterId,
		collaboratorAllCenters: false,
		collaboratorCenterIds: [],
		optionType: EventOptionType.date,
		frequency: ScheduleFrequency.oneOff,
		// notification emails
		mainNotificationEmail: userEmail,
		extraNotificationEmails: [],
		...partialEvent,
	};
}

export function EmptyScheduleFactory(): Pick<
	EventSchedule,
	| 'startDate'
	| 'endDate'
	| 'startTime'
	| 'endTime'
	| 'isAllDay'
	| 'isDateTBC'
	| 'isSingleDay'
	| 'isTimeTBC'
> {
	return {
		// Date
		isDateTBC: false,
		isSingleDay: true,
		startDate: null,
		endDate: null,

		// Time
		isTimeTBC: false,
		isAllDay: false,
		startTime: null,
		endTime: null,
	};
}

export function EmptyFeeTypeFactory({
	isTicketing,
	isByFeeType,
	isInternalEvent,
}: {
	isTicketing: boolean;
	isByFeeType: boolean;
	isInternalEvent?: boolean;
}): Pick<
	EventFeeType,
	| 'title'
	| 'description'
	| 'fee'
	| 'quota'
	| 'numOfPeople'
	| 'minPurchase'
	| 'maxPurchase'
> {
	return {
		title: EmptyMultiLangText,
		description: EmptyMultiLangText,
		// fee cannot not be null/undefined for event application creation of internal event
		fee: isInternalEvent ? 0 : null,
		quota: isByFeeType ? 0 : null,
		numOfPeople: null,
		minPurchase: null,
		maxPurchase: null,
	};
}

export function EmptyOptionFactory(): EventOption {
	return {
		title: cloneDeep(EmptyMultiLangText),
		position: 0,
		remarks: {},
		schedules: [
			{
				...EmptyScheduleFactory(),
				position: 0,
			},
		],
		startDate: null,
		endDate: null,
		totalQuota: null,
		feeTypes: [
			EmptyFeeTypeFactory({
				isTicketing: true,
				isByFeeType: false,
			}),
		],
	};
}

export enum EventEditStep {
	basicInfo = 'basicInfo',
	options = 'options',
	schedule = 'schedule',
	admission = 'admission',
	details = 'details',
	application = 'application',
	payment = 'payment',
	otherSettings = 'otherSettings',
}

export const EVENT_STEPS = [
	EventEditStep.basicInfo,
	EventEditStep.options,
	EventEditStep.schedule,
	EventEditStep.admission,
	EventEditStep.payment,
	EventEditStep.details,
	EventEditStep.application,
	EventEditStep.otherSettings,
];

export import EventValidationStatus = MultiStepFormValidationStatus;

export type EventActivityTheme = Constant;
export type EventActivityType = Constant;
export type EventServiceType = Constant;
export type EventTargetAudience = Constant;

export enum EventApplicationStatus {
	pending = 'pending',
	accepted = 'accepted',
	reserved = 'reserved',
	declined = 'declined',
	onHold = 'onHold',
	cancelled = 'cancelled',
}

export interface EventAppliedSchedule {
	id: string;
	eventApplication?: EventApplication;
	scheduleId: string;
	status: EventApplicationStatus;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface EventAppliedItem {
	id: string;
	eventApplication?: EventApplication;
	eventApplicationId: string;
	feeTypeId?: string;
	quantity?: number;
	ticketItems?: EventTicketItem[];
	createdAt: Date;
	updatedAt: Date;
}

export interface EventTicketItem {
	id: string;
	user?: Volunteer;
	userId: string;
	ticketNo: string;
	eventId: string; //for easier list retrieval
	optionId: string; //for easier list retrieval
	applicationId: string; //for easier list retrieval
	appliedItem?: Partial<EventAppliedItem>;
	appliedItemId: string;
	redeemedAt?: Date;
	remarks?: string;
	createdAt: Date;
	updatedAt: Date;
}

export interface EventApplication {
	id: string;
	status: EventApplicationStatus;
	eventId: string;
	optionId: string;
	appliedItems: EventAppliedItem[];
	user?: AppUserProfile;
	userId: string;
	firstName?: string;
	lastName?: string;
	chineseName?: string;
	mobileNumber?: string;
	email?: string;
	tnc: string[];
	additionalQuestions?: AdditionalQuestionsData[];

	// Parental Consent
	parentalConsent?: boolean;
	parentInfo?: any;

	appliedSchedules?: EventAppliedSchedule[];
	totalPrice?: number;

	data?: any;
	remarks?: string;
	reason: string | null;
	paymentId?: string;
	paymentTypeId?: string;
	totalNumOfTickets?: number;
	paymentStatus?: ApplicationPaymentStatus;
	paymentMethodValue?: PaymentMethodValues;
	adjustedFee?: number | null;

	createdAt: Date;
	updatedAt: Date;
}

export interface EventApplicationDto {
	id?: string;
	eventId: string;
	optionId: string;
	userId?: string;
	firstName?: string;
	lastName?: string;
	chineseName?: string;
	mobileNumber?: string;
	email?: string;
	paymentStatus?: EventPaymentStatusCode;
	paymentMethodValue?: PaymentMethodValues;
	tnc?: string[];
	additionalQuestions?: AdditionalQuestionData[];
	appliedItems: ApplyEventItemDto[];
	// schedule: {};
	isWalkIn?: boolean;
	isBulk?: boolean;
}

export interface EventApplicationUpdateDto {
	id?: string;
	firstName?: string;
	lastName?: string;
	chineseName?: string;
	mobileNumber?: string;
	email?: string;
	status?: EventApplicationStatus;
	optionId?: string;
	paymentStatus?: ApplicationPaymentStatus;
	appliedItems?: Partial<EventAppliedItem>[];
	paymentTypeId?: string;
	remarks?: string;
	reason?: string | null;
	tnc?: string[];
	additionalQuestions?: AdditionalQuestionsData[];
	parentInfo?: any;
}

export interface EventAppSchedulesDto {
	[sessionKey: string]: string[];
}

export interface AdditionalQuestionsData {
	key: string;
	value: string | number | boolean | string[];
}

export enum ApplicationPaymentStatus {
	free = 'free',
	unpaid = 'unpaid',
	paid = 'paid',
	waived = 'waived',
	refunded = 'refunded',
	partiallyRefunded = 'partiallyRefunded',
	na = 'na',
}

export const AllApplicationPaymentStatuses: ApplicationPaymentStatus[] = [
	ApplicationPaymentStatus.free,
	ApplicationPaymentStatus.unpaid,
	ApplicationPaymentStatus.paid,
	ApplicationPaymentStatus.waived,
	ApplicationPaymentStatus.refunded,
	ApplicationPaymentStatus.partiallyRefunded,
	ApplicationPaymentStatus.na,
];

export const EventOtherReasonCode = 'others';
export const EventAvailableAbsentReasonCodes = [
	'clashOfSchedule',
	'workCommitment',
	EventOtherReasonCode,
];

export interface EventAttendance {
	id: string;
	user?: Volunteer;
	userId: string;
	eventId: string; //for easier list retrieval
	scheduleId: string; //for easier list retrieval
	scheduleInstanceId: string; //schedule_instance id from event-service

	startDate: string;

	appliedSchedule?: EventAppliedSchedule;
	appliedScheduleId: string;

	remarks?: string;
	absent?: boolean;
	absentReason?: string;

	checkInAt?: Date;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface ParticipatedEvent {
	title: MultiLangText;
	type: EventAdmissionType;
	progress: EventProgress;
	startDate: Date;
	endDate: Date;
	applications: {
		application: EventApplication;
		optionTitle: MultiLangText;
	}[];

	attendedSchedules: number;
	completedSchedules: number;
}

export interface QrCodeMap {
	key: string;
	value: string;
}

export interface EventQrCodeCheckIn {
	id: string;
	time: string;
}

export enum EventPaymentStatusCode {
	free = 'free',
	unpaid = 'unpaid',
	paid = 'paid',
	waived = 'waived',
	refunded = 'refunded',
	partiallyRefunded = 'partiallyRefunded',
	na = 'na',
}

export interface AdditionalQuestionData {
	key: string;
	value: boolean | string | number;
}

export interface ApplyEventItemDto {
	feeTypeId: string;
	quantity?: number;
}

export const EventProgressChipStyle = {
	[EventProgress.ongoing]: ACTIVE_CHIP,
	[EventProgress.scheduled]: NEUTRAL_CHIP,
	[EventProgress.completed]: INACTIVE_CHIP,
	[EventProgress.cancelled]: NEGATIVE_CHIP,
};

export const EventScheduleProgressChipStyle = {
	[EventScheduleProgressCode.ongoing]: ACTIVE_CHIP,
	[EventScheduleProgressCode.scheduled]: NEUTRAL_CHIP,
	[EventScheduleProgressCode.completed]: INACTIVE_CHIP,
	[EventScheduleProgressCode.cancelled]: NEGATIVE_CHIP,
};

export const EventStatusChipStyle = {
	[EventStatusCode.draft]: NEUTRAL_CHIP,
	[EventStatusCode.published]: ACTIVE_CHIP,
	[EventStatusCode.completed]: INACTIVE_CHIP,
	[EventStatusCode.closed]: INACTIVE_CHIP,
	[EventStatusCode.cancelled]: NEGATIVE_CHIP,
	[EventStatusCode.full]: INACTIVE_CHIP,
};

export const EventApplicationStatusChipStyle = {
	[EventApplicationStatus.accepted]: ACTIVE_CHIP,
	[EventApplicationStatus.declined]: NEGATIVE_CHIP,
	[EventApplicationStatus.cancelled]: NEGATIVE_CHIP,
	[EventApplicationStatus.pending]: NEUTRAL_CHIP,
	[EventApplicationStatus.reserved]: NEUTRAL_CHIP,
	[EventApplicationStatus.onHold]: NEUTRAL_CHIP,
};

export const EventApplicationPaymentStatusChipStyle = {
	[ApplicationPaymentStatus.free]: ACTIVE_CHIP,
	[ApplicationPaymentStatus.unpaid]: NEUTRAL_CHIP,
	[ApplicationPaymentStatus.paid]: ACTIVE_CHIP,
	[ApplicationPaymentStatus.waived]: ACTIVE_CHIP,
	[ApplicationPaymentStatus.refunded]: NEGATIVE_CHIP,
	[ApplicationPaymentStatus.partiallyRefunded]: ACTIVE_CHIP,
	[ApplicationPaymentStatus.na]: NEUTRAL_CHIP,
};

export interface EventAttendanceImportTableItem
	extends AttendanceImportTableItem {
	checkInAt?: Date;
}

export interface EventApplicationCache {
	[appUserId: string]: { [applicationId: string]: EventApplication };
}
